import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { ChevronLeft, ChevronRight } from 'lucide-react';

const API = window.location.origin + '/api';
const BACKEND_URL = window.location.origin;

// Helper to get full image URL
const getImageUrl = (url) => {
  if (!url) return null;
  if (url.startsWith('http')) return url;
  if (url.startsWith('/')) return `${BACKEND_URL}${url}`;
  return url;
};

export default function BannerSlider({ audience = 'customer' }) {
  const [banners, setBanners] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isLoading, setIsLoading] = useState(true);

  const fetchBanners = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/banners?audience=${audience}`);
      // Filter out banners without valid image URLs
      const validBanners = (response.data || []).filter(b => b.image_url);
      setBanners(validBanners);
    } catch (error) {
      console.error('Failed to fetch banners:', error);
    } finally {
      setIsLoading(false);
    }
  }, [audience]);

  useEffect(() => {
    fetchBanners();
  }, [fetchBanners]);

  // Auto-slide every 5 seconds
  useEffect(() => {
    if (banners.length <= 1) return;
    
    const interval = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % banners.length);
    }, 5000);

    return () => clearInterval(interval);
  }, [banners.length]);

  const goToSlide = (index) => {
    setCurrentIndex(index);
  };

  const goToPrevious = () => {
    setCurrentIndex((prev) => (prev - 1 + banners.length) % banners.length);
  };

  const goToNext = () => {
    setCurrentIndex((prev) => (prev + 1) % banners.length);
  };

  const handleBannerClick = (banner) => {
    if (banner.link_url) {
      window.open(banner.link_url, '_blank');
    }
  };

  const handleImageError = async (bannerId) => {
    // Remove from local state immediately for better UX
    setBanners(prev => {
      const filtered = prev.filter(b => b.id !== bannerId);
      // Adjust index if needed
      if (currentIndex >= filtered.length && filtered.length > 0) {
        setCurrentIndex(filtered.length - 1);
      } else if (filtered.length === 0) {
        setCurrentIndex(0);
      }
      return filtered;
    });
    
    // Report broken image to backend for cleanup
    try {
      await axios.post(`${API}/banners/report-broken`, { banner_id: bannerId });
    } catch (error) {
      // Silently ignore - banner already removed from UI
    }
  };

  if (isLoading) {
    return (
      <div className="w-full aspect-[3/1] bg-gradient-to-r from-slate-200 to-slate-100 rounded-2xl animate-pulse" />
    );
  }

  if (banners.length === 0) {
    return null; // Don't render anything if no banners
  }

  return (
    <div className="relative w-full overflow-hidden rounded-2xl" data-testid="banner-slider">
      {/* Banner Images */}
      <div 
        className="flex transition-transform duration-500 ease-out"
        style={{ transform: `translateX(-${currentIndex * 100}%)` }}
      >
        {banners.map((banner) => (
          <div
            key={banner.id}
            className="w-full flex-shrink-0 cursor-pointer"
            onClick={() => handleBannerClick(banner)}
            data-testid={`banner-${banner.id}`}
          >
            <img
              src={getImageUrl(banner.image_url)}
              alt={banner.title || 'Promotional Banner'}
              className="w-full h-auto object-contain"
              loading="lazy"
              onError={() => handleImageError(banner.id)}
            />
          </div>
        ))}
      </div>

      {/* Navigation Arrows - Only show if more than 1 banner */}
      {banners.length > 1 && (
        <>
          <button
            onClick={goToPrevious}
            className="absolute left-2 top-1/2 -translate-y-1/2 w-8 h-8 sm:w-10 sm:h-10 bg-black/30 hover:bg-black/50 rounded-full flex items-center justify-center text-white transition-colors"
            aria-label="Previous banner"
            data-testid="banner-prev"
          >
            <ChevronLeft className="w-5 h-5 sm:w-6 sm:h-6" />
          </button>
          <button
            onClick={goToNext}
            className="absolute right-2 top-1/2 -translate-y-1/2 w-8 h-8 sm:w-10 sm:h-10 bg-black/30 hover:bg-black/50 rounded-full flex items-center justify-center text-white transition-colors"
            aria-label="Next banner"
            data-testid="banner-next"
          >
            <ChevronRight className="w-5 h-5 sm:w-6 sm:h-6" />
          </button>
        </>
      )}

      {/* Dots Indicator */}
      {banners.length > 1 && (
        <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-2">
          {banners.map((banner, index) => (
            <button
              key={banner.id || `banner-dot-${index}`}
              onClick={() => goToSlide(index)}
              className={`w-2 h-2 rounded-full transition-all ${
                index === currentIndex 
                  ? 'bg-white w-6' 
                  : 'bg-white/50 hover:bg-white/75'
              }`}
              aria-label={`Go to slide ${index + 1}`}
              data-testid={`banner-dot-${index}`}
            />
          ))}
        </div>
      )}
    </div>
  );
}
